import { z } from "zod";
import { isSafeDisplayText, lafayettePresentableUiPartNames, runStatusDegradedReasons, runStatusSafeTextKeys, runStatusSuggestedActions, } from "./agent-ui-parts.js";
// The messaging contract of Milestone C (CHT-724..729; docs/architecture/milestone-c-contracts.md, sections 3 and 4;
// ADRs 0009, 0010 and 0014). FROZEN at the Wave 0 commit. Lanes A (backend messaging), B (mobile conversation) and E (staff
// web) build on it. A change needs a reviewed edit of the contract document first.
//
// Rules:
// - Stream owns message TRANSPORT only. Postgres owns business truth. Nothing here carries a payment, request, handoff or
//   trip status that a client may trust: a client reads the live entity by id.
// - Every id below is deterministic and holds no personal data: a namespace, a fixed word and a Lafayette uuid.
// - The namespace keeps environments apart inside one Stream app (dev and staging share the app "Lafayette Dev").
// - Times are ISO 8601 text, like `customer-memory.ts`, because the same schema parses the JSON in the API and the clients.
// - This package cannot import the domain. The code lists below repeat domain tuples; a parity test in
//   `@lafayette/agent-contract` (`milestone-c-parity.test.ts`) fails when one differs.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
// --- namespace and ids --------------------------------------------------------------------------------------------------
/** The environment namespace: `dev`, `stg`, `prd`, `tst`, or an operator override such as `devsk`. Lowercase, 2 to 8. */
export const MESSAGING_NAMESPACE_PATTERN = /^[a-z][a-z0-9]{1,7}$/u;
export const messagingNamespaceSchema = z.string().regex(MESSAGING_NAMESPACE_PATTERN);
/** The default namespace of each `LAFAYETTE_ENV`. `LAFAYETTE_STREAM_NAMESPACE` may override it (never in production). */
export const DEFAULT_MESSAGING_NAMESPACES = Object.freeze({
    development: "dev",
    test: "tst",
    staging: "stg",
    production: "prd",
});
/** The fixed actor words. A Stream user id is `<namespace>-<word>` or `<namespace>-<cust|staff>-<uuid>`. */
export const messagingSystemActors = Object.freeze([
    "lafayette-ai",
    "lafayette-system",
    "lafayette-copilot",
]);
/** `<ns>-cust-<customerId>`, `<ns>-staff-<staffUserId>`, `<ns>-lafayette-ai`, `<ns>-lafayette-system`, `<ns>-lafayette-copilot`. */
export const STREAM_USER_ID_PATTERN = /^[a-z][a-z0-9]{1,7}-(?:(?:cust|staff)-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|lafayette-(?:ai|system|copilot))$/u;
export const streamUserIdSchema = z.string().regex(STREAM_USER_ID_PATTERN);
/** The two Stream channel types Lafayette uses. `messaging` is the customer thread; `staff_copilot` is private to one staff member. */
export const messagingChannelTypes = Object.freeze(["messaging", "staff_copilot"]);
/**
 * Customer channel: `<ns>-conv-<conversationId>` (one channel per Lafayette conversation; one open conversation per customer).
 * Copilot channel: `<ns>-cop-<first 32 hex of sha256("<staffUserId>:<conversationId>")>`. Both fit the database check
 * `conversations_stream_channel_id_format` (`^[A-Za-z0-9_!-]{1,64}$`).
 */
export const CUSTOMER_CHANNEL_ID_PATTERN = /^[a-z][a-z0-9]{1,7}-conv-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
export const COPILOT_CHANNEL_ID_PATTERN = /^[a-z][a-z0-9]{1,7}-cop-[0-9a-f]{32}$/u;
export const customerChannelIdSchema = z.string().regex(CUSTOMER_CHANNEL_ID_PATTERN);
export const copilotChannelIdSchema = z.string().regex(COPILOT_CHANNEL_ID_PATTERN);
/**
 * Message ids. Lafayette code ALWAYS sets the id; the SDK default (`<userId>-<uuid>`) is too long for the message source
 * contract (`^[A-Za-z0-9:_.-]{1,64}$`, `conversation-messages.ts` of the application).
 * - customer and staff messages from a client: a lowercase uuid v4;
 * - an AI reply: `lfai-<replyId>`; a system notice: `lfsys-<noticeId>`; a copilot answer or draft: `lfcop-<uuid>`.
 */
export const MESSAGE_ID_PATTERN = /^(?:(?:lfai|lfsys|lfcop)-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
export const messageIdSchema = z.string().regex(MESSAGE_ID_PATTERN);
/** Who wrote a message, derived by the server from the Stream user id (never from message data). */
export const messageAuthorKinds = Object.freeze([
    "CUSTOMER",
    "LAFAYETTE_AI",
    "CONCIERGE",
    "SYSTEM",
    "COPILOT",
]);
// --- session (token) endpoints ------------------------------------------------------------------------------------------
/** Stream user tokens are short: the client refreshes through the same session route. */
export const MESSAGING_TOKEN_TTL_SECONDS = Object.freeze({ customer: 6 * 3600, staff: 3600 });
/**
 * `POST /customer/messaging/session`. The server ensures the conversation, the Stream user, the channel binding and the
 * members, then signs a token for the caller's OWN Stream user. `apiKey` is the public Stream key (no EXPO_PUBLIC variable
 * is needed). The server secret never leaves the server.
 */
export const customerMessagingSessionSchema = z.strictObject({
    apiKey: z.string().min(1).max(64),
    userId: streamUserIdSchema,
    token: z.string().min(16).max(2048),
    expiresAt: instant,
    conversationId: uuid,
    channel: z.strictObject({ type: z.literal("messaging"), id: customerChannelIdSchema }),
    /** The neutral customer status (`toCustomerConversationView`): AI_ASSISTANT, CONNECTING_TO_CONCIERGE, CONCIERGE_ACTIVE. */
    conversationStatusCode: z.enum(["AI_ASSISTANT", "CONNECTING_TO_CONCIERGE", "CONCIERGE_ACTIVE"]),
});
/** `POST /staff/messaging/session`. Only an ACTIVE staff user with `concierge` or `supervisor` (and aal2 outside development). */
export const staffMessagingSessionSchema = z.strictObject({
    apiKey: z.string().min(1).max(64),
    userId: streamUserIdSchema,
    token: z.string().min(16).max(2048),
    expiresAt: instant,
});
// --- the Lafayette message envelope (custom field `lf` of a Stream message) ---------------------------------------------
/**
 * Stream limits the custom data of one message to 5 KB. The server measures the JSON of `lf` in UTF-8 bytes and keeps it at
 * or below this number. Over the limit, it drops trailing part references (counted as `PART_TOO_LARGE`) and keeps the text.
 */
export const MESSAGE_ENVELOPE_MAX_BYTES = 4_500;
/** At most this many part references in one envelope, and this many entity references across all of them. */
export const MESSAGE_ENVELOPE_LIMITS = Object.freeze({
    maxParts: 8,
    maxRefs: 12,
    maxLabelChars: 40,
    maxTextChars: 4_000,
});
/** The AI message state that a client shows. One message per reply; the server updates it in place. */
export const aiMessageStates = Object.freeze([
    "THINKING",
    "STREAMING",
    "COMPLETED",
    "DEGRADED",
    "FAILED",
    "INTERRUPTED",
    "STOPPED_FOR_CONCIERGE",
    "LIMITED",
]);
/** A client treats every other state as final: no spinner, no typing indicator. */
export const aiMessageOpenStates = Object.freeze(["THINKING", "STREAMING"]);
/** The phase 4 degraded reasons plus the two that only a Stream reply has. */
export const aiReplyDegradedReasons = Object.freeze([
    ...runStatusDegradedReasons,
    "DAILY_LIMIT_REACHED",
    "RUN_INTERRUPTED",
]);
export const aiReplySafeTextKeys = Object.freeze([
    ...runStatusSafeTextKeys,
    "agent.daily_limit",
    "agent.interrupted",
]);
export const aiReplyOutcomeSchema = z.strictObject({
    reason: z.enum(aiReplyDegradedReasons),
    safeTextKey: z.enum(aiReplySafeTextKeys),
    suggestedAction: z.enum(runStatusSuggestedActions),
});
/** The entity kinds that a part reference may name. A client reads the live entity by id before it shows status. */
export const messagePartRefTypes = Object.freeze([
    "CATALOG_ITEM",
    "CONCIERGE_REQUEST",
    "CONVERSATION",
    "ITINERARY",
    "TRIP",
    "PAYMENT_REQUEST",
]);
const label = z
    .string()
    .min(1)
    .max(MESSAGE_ENVELOPE_LIMITS.maxLabelChars)
    .refine(isSafeDisplayText, "label must be plain display text");
/**
 * A compact reference to one validated UI part (phase-4-ui-protocol.md section 3). It carries the part name, its schema
 * version, the canonical ids and, per id, a short historical label for the "no longer available" state. The full part is
 * never stored in Stream: the renderer hydrates canonical state by id (CHT-728).
 */
export const messagePartRefSchema = z.strictObject({
    k: z.enum(lafayettePresentableUiPartNames),
    v: z.number().int().min(1).max(99),
    refs: z
        .array(z.strictObject({
        type: z.enum(messagePartRefTypes),
        id: uuid,
        label: label.nullable(),
    }))
        .min(1)
        .max(MESSAGE_ENVELOPE_LIMITS.maxParts),
});
/** The fixed codes of a customer-safe system notice. The words live in the clients. No routing detail ever shows. */
export const systemNoticeCodes = Object.freeze([
    "CONNECTING_TO_CONCIERGE",
    "CONCIERGE_JOINED",
    "CONCIERGE_CHANGED",
    "AI_RESUMED",
]);
export const aiReplyEnvelopeSchema = z
    .strictObject({
    v: z.literal(1),
    kind: z.literal("AI_REPLY"),
    replyId: uuid,
    state: z.enum(aiMessageStates),
    /** Grows by one with every update; a client ignores an update with a lower `seq` than one it has shown. */
    seq: z.number().int().min(0).max(100_000),
    // Stream stores an EMPTY array of custom data as `null` (live finding 2026-10-16): `null` reads as no parts.
    parts: z.preprocess((value) => (value === null ? [] : value), z.array(messagePartRefSchema).max(MESSAGE_ENVELOPE_LIMITS.maxParts)),
    /** Set exactly when `state` is DEGRADED, FAILED, INTERRUPTED or LIMITED. */
    outcome: aiReplyOutcomeSchema.nullable(),
})
    .refine((value) => value.parts.reduce((sum, part) => sum + part.refs.length, 0) <=
    MESSAGE_ENVELOPE_LIMITS.maxRefs, "too many part references")
    .refine((value) => (value.outcome !== null) ===
    ["DEGRADED", "FAILED", "INTERRUPTED", "LIMITED"].includes(value.state), "outcome must be set exactly for a degraded, failed, interrupted or limited reply");
export const systemNoticeEnvelopeSchema = z.strictObject({
    v: z.literal(1),
    kind: z.literal("SYSTEM_NOTICE"),
    noticeId: uuid,
    code: z.enum(systemNoticeCodes),
});
/** Copilot messages exist only in a `staff_copilot` channel. A draft's text is the Stream message text (at most 4,000). */
export const copilotEnvelopeSchema = z.strictObject({
    v: z.literal(1),
    kind: z.enum(["COPILOT_QUESTION", "COPILOT_ANSWER", "COPILOT_DRAFT"]),
    runId: uuid.nullable(),
    conversationId: uuid,
    /** Set (to `true`) when the stored text is cut to the channel's message limit; the full text was shown in the stream (CHT-733 review). */
    truncated: z.literal(true).optional(),
});
export const lafayetteMessageEnvelopeSchema = z.union([
    aiReplyEnvelopeSchema,
    systemNoticeEnvelopeSchema,
    copilotEnvelopeSchema,
]);
/** The custom field name of the envelope on a Stream message. */
export const MESSAGE_ENVELOPE_FIELD = "lf";
// --- conversation replies (the correlation record, table `conversation_replies`) ----------------------------------------
/**
 * One row per eligible customer message (unique by source message). `PENDING` and `RUNNING` are open. The domain tuple of
 * lane A must equal this list (parity test).
 */
export const conversationReplyStates = Object.freeze([
    "PENDING",
    "RUNNING",
    "COMPLETED",
    "DEGRADED",
    "FAILED",
    "INTERRUPTED",
    "FENCED",
    "CAPPED",
    "EXPIRED",
    "SKIPPED",
]);
// --- Stream webhooks ----------------------------------------------------------------------------------------------------
/** The request headers of a Stream webhook (lowercase, as Hono reads them). The signature covers the RAW body. */
export const STREAM_WEBHOOK_HEADERS = Object.freeze({
    signature: "x-signature",
    webhookId: "x-webhook-id",
    attempt: "x-webhook-attempt",
    apiKey: "x-api-key",
});
/** The event types the API acts on. Every other type is answered 200 and recorded as `ignored`. */
export const handledStreamEventTypes = Object.freeze([
    "message.new",
    "message.updated",
    "message.deleted",
    "message.undeleted",
    "channel.deleted",
    "channel.truncated",
    "member.added",
    "member.removed",
]);
const streamId = z.string().min(1).max(255);
const streamTime = z.string().min(1).max(40);
/**
 * The SUBSET of a Stream webhook body that the API reads. NOT strict: Stream adds fields, and unknown keys are stripped.
 * `message.text` and attachment content are deliberately not read: the webhook path never holds message text. History and
 * text come from the messaging port (`queryMessages`) when a run or an extraction needs them. The one thing taken from the text is
 * a flag: `blankText: true` when the body names a text that is a string, empty or only white space (D-A4). The text itself is dropped by
 * the parse. A body without a `text` key, or with a `text` that is not a string (`null`, a number), sets no flag (nothing is known), so
 * the message is judged as before and the parse never fails because of it.
 */
export const streamWebhookEventSchema = z.object({
    type: z.string().min(1).max(64),
    cid: z.string().min(1).max(140).optional(),
    channel_type: z.string().min(1).max(64).optional(),
    channel_id: z.string().min(1).max(64).optional(),
    created_at: streamTime.optional(),
    user: z.object({ id: streamId }).optional(),
    member: z.object({ user_id: streamId }).optional(),
    message: z
        .object({
        id: streamId,
        type: z.string().min(1).max(32).optional(),
        parent_id: streamId.nullable().optional(),
        user: z.object({ id: streamId }).optional(),
        created_at: streamTime.optional(),
        updated_at: streamTime.optional(),
        deleted_at: streamTime.nullable().optional(),
        attachments: z.array(z.unknown()).max(30).optional(),
        // Not `z.string()`: a `null` or a number must not fail the whole parse (a 400 that loses the projection). Only a string is judged.
        text: z.unknown().optional(),
    })
        .transform(({ text, ...message }) => typeof text === "string" && text.trim() === ""
        ? { ...message, blankText: true }
        : message)
        .optional(),
});
/**
 * The SUBSET of the body of Stream's BEFORE-MESSAGE-SEND webhook that the API reads (CHT-732; checked 2026-10-16 against
 * https://getstream.io/chat/docs/node/before-message-send-webhook/). The body is `{ message, user, channel, request_info }`: `user` is the
 * author, `channel` names the channel (`cid`, `id`, `type`), and `message` has `id`, `text`, `type` and more. NOT strict: unknown keys are
 * stripped. `message.text` and the attachments are deliberately not read: the guard decides on who writes where, never on what. A
 * body that does not parse is answered without a decision (Stream saves the message: it fails open on every error of the endpoint).
 */
export const streamBeforeSendSchema = z.object({
    user: z.object({ id: streamId }).optional(),
    channel: z.object({
        id: z.string().min(1).max(64),
        type: z.string().min(1).max(64),
        cid: z.string().min(1).max(140).optional(),
    }),
    message: z
        .object({
        // The documented body carries `"id": ""` (Stream assigns the id after the hook), and a client-chosen id has no fixed shape. The
        // guard does not use it: anything here must parse, because a 400 makes Stream save the message (fail open).
        id: z.string().max(255).optional(),
        type: z.string().min(1).max(32).optional(),
        // The live body (staging proof, 2026-10-16) fails the strict id shape here: Stream fills `message.user` before it has the author
        // (like `"id": ""`). The author is the top-level `user`; this one is only a fallback and must never make the body fail.
        user: z
            .object({ id: z.string().max(255).optional() })
            .nullable()
            .optional(),
    })
        .optional(),
    request_info: z.object({ type: z.string().max(32).optional() }).optional(),
});
/**
 * What the API answers the hook. A rejection is HTTP 200 with a message whose `type` is `error` (the documented way); the `text` is shown to
 * the sender. Letting a message through is HTTP 200 with an empty JSON object: no field is rewritten. (The documentation names no
 * pass-through body; an empty object rewrites nothing. The staging proof confirms it with a real send.)
 */
export const streamBeforeSendRejectionSchema = z.strictObject({
    message: z.strictObject({ type: z.literal("error"), text: z.string().min(1).max(240) }),
});
export const STREAM_BEFORE_SEND_PASS = Object.freeze({});
export const streamBeforeSendRejection = (text) => ({
    message: { type: "error", text },
});
